import React from 'react';
import {
  Box,
  Checkbox,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { IconLink, IconLock, IconLinkOff } from '@tabler/icons-react';
import type { TablerIconsProps } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { LinkShareMode } from '@/types/Product.model';
import { hexToRgba } from '@/utils/colorUtils';

type LinkShareOptionCardProps = {
  icon: React.ComponentType<TablerIconsProps>;
  title: string;
  note: string;
  accentColor: string;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: () => void;
};

function LinkShareOptionCard({
  icon: Icon,
  title,
  note,
  accentColor,
  selected = false,
  selectable = false,
  onSelect,
}: LinkShareOptionCardProps) {
  return (
    <Paper
      withBorder
      p="sm"
      style={{
        backgroundColor: selected
          ? hexToRgba(accentColor, 0.06)
          : 'var(--mantine-color-default)',
        borderColor: selected ? accentColor : 'var(--mantine-color-default-border)',
        borderWidth: selected ? 2 : 1,
        cursor: selectable ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      }}
      onClick={selectable ? onSelect : undefined}
      role={selectable ? 'radio' : undefined}
      aria-checked={selectable ? selected : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={
        selectable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon
          variant="light"
          color={selected ? undefined : 'gray'}
          size="md"
          radius="md"
          style={
            selected
              ? { backgroundColor: hexToRgba(accentColor, 0.12), color: accentColor }
              : undefined
          }
        >
          <Icon size={16} stroke={1.75} />
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600}>
            {title}
          </Text>
          <Text size="xs" c="dimmed" mt={4} style={{ lineHeight: 1.45 }}>
            {note}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
}

const PRIVATE_LINK_MODES: {
  value: LinkShareMode;
  icon: React.ComponentType<TablerIconsProps>;
  titleKey: string;
  noteKey: string;
}[] = [
  {
    value: 'open',
    icon: IconLink,
    titleKey: 'editProduct.linkShare.modeOpen',
    noteKey: 'editProduct.linkShare.modeOpenHelp',
  },
  {
    value: 'password',
    icon: IconLock,
    titleKey: 'editProduct.linkShare.modePassword',
    noteKey: 'editProduct.linkShare.modePasswordHelp',
  },
  {
    value: 'restricted',
    icon: IconLinkOff,
    titleKey: 'editProduct.linkShare.modeRestricted',
    noteKey: 'editProduct.linkShare.modeRestrictedHelp',
  },
];

export type LinkAccessControlProps = {
  accentColor: string;
  linkShareMode: LinkShareMode;
  onLinkShareModeChange: (mode: LinkShareMode) => void;
  /** When true (marketplace public), only the open-link card is shown. */
  isPublic?: boolean;
  sectionHelp?: string;
  showSectionTitle?: boolean;
  passwordAlreadySet?: boolean;
  updatePassword?: boolean;
  onUpdatePasswordChange?: (checked: boolean) => void;
  password?: string;
  onPasswordChange?: (value: string) => void;
};

export function LinkAccessControl({
  accentColor,
  linkShareMode,
  onLinkShareModeChange,
  isPublic = false,
  sectionHelp,
  showSectionTitle = false,
  passwordAlreadySet = false,
  updatePassword = false,
  onUpdatePasswordChange,
  password = '',
  onPasswordChange,
}: LinkAccessControlProps) {
  const { t } = useTranslation();

  const handleModeChange = (mode: LinkShareMode) => {
    onLinkShareModeChange(mode);
    if (mode === 'open' || mode === 'restricted') {
      onUpdatePasswordChange?.(false);
      onPasswordChange?.('');
    }
  };

  return (
    <Stack gap="sm">
      {showSectionTitle ? (
        <>
          <Text size="sm" fw={600} mt="xs">
            {t('editProduct.linkShare.sectionTitle')}
          </Text>
          <Text size="xs" c="dimmed">
            {sectionHelp ?? t('editProduct.linkShare.sectionHelp')}
          </Text>
        </>
      ) : sectionHelp ? (
        <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
          {sectionHelp}
        </Text>
      ) : null}

      {isPublic ? (
        <LinkShareOptionCard
          icon={IconLink}
          title={t('editProduct.linkShare.modeOpen')}
          note={t('editProduct.linkShare.publicOpenNote')}
          accentColor={accentColor}
          selected
        />
      ) : (
        <Stack gap="xs" role="radiogroup" aria-label={t('editProduct.linkShare.sectionTitle')}>
          {PRIVATE_LINK_MODES.map((option) => (
            <LinkShareOptionCard
              key={option.value}
              icon={option.icon}
              title={t(option.titleKey)}
              note={t(option.noteKey)}
              accentColor={accentColor}
              selected={linkShareMode === option.value}
              selectable
              onSelect={() => handleModeChange(option.value)}
            />
          ))}
        </Stack>
      )}

      {!isPublic && linkShareMode === 'password' && onPasswordChange ? (
        <Box>
          {passwordAlreadySet && onUpdatePasswordChange ? (
            <Checkbox
              label={t('editProduct.linkShare.changePassword')}
              checked={updatePassword}
              onChange={(e) => {
                onUpdatePasswordChange(e.currentTarget.checked);
                if (!e.currentTarget.checked) onPasswordChange('');
              }}
              mb="sm"
            />
          ) : null}
          {(!passwordAlreadySet || updatePassword) && (
            <PasswordInput
              label={t('editProduct.linkShare.passwordLabel')}
              description={t('editProduct.linkShare.passwordDescription')}
              value={password}
              onChange={(e) => onPasswordChange(e.currentTarget.value)}
              autoComplete="new-password"
            />
          )}
        </Box>
      ) : null}
    </Stack>
  );
}
